<script setup>
import { MessageSquareText, ChevronRight } from '@lucide/vue';

const props = defineProps({
    open: { type: Boolean, default: false },
    drawerWidth: { type: String, default: 'min(420px, 100%)' },
    // 'error' | 'success' | null — shows a colored dot when the drawer is
    // collapsed and a reply or error arrived while the user wasn't looking.
    notice: { type: String, default: null },
});

defineEmits(['toggle']);
</script>

<template>
    <button
        type="button"
        class="aia-d2-trigger aia-gradient-bg-accent aia-shimmer group fixed top-1/2 flex cursor-pointer flex-col items-center gap-2 py-4 px-2.5 rounded-l-2xl border border-r-0 border-[var(--border)] text-white shadow-[-12px_0_40px_-12px_rgba(192,38,211,0.45)]"
        :data-open="open"
        :style="{
            '--aia-d2-drawer-w': drawerWidth,
            zIndex: 'var(--aia-z)',
        }"
        :aria-label="open ? 'Close chat' : 'Open chat with the assistant'"
        :aria-expanded="open"
        @click="$emit('toggle')"
    >
        <!-- Top icon: chat bubble when closed, animates subtly on hover.
             Carries a notification dot at its top-right when collapsed and a
             reply (accent) or error (destructive) arrived while away. -->
        <span class="relative inline-flex">
            <MessageSquareText
                :size="20"
                :stroke-width="2"
                class="transition-transform duration-300 group-hover:scale-110"
            />
            <span
                v-if="notice && !open"
                class="aia-d2-badge"
                :style="{ color: notice === 'error' ? '#ef4444' : '#22c55e' }"
                aria-hidden="true"
            />
        </span>

        <!-- Vertical label, letters stacked upright (writing-mode: vertical +
             text-orientation: upright). `white-space: nowrap` keeps it in a
             SINGLE column — otherwise the space in "ASK AI" lets it wrap into
             two columns on narrow/mobile viewports. Mirrors to "Close" when the
             drawer is open so the grabber doubles as the close affordance. -->
        <span
            class="aia-d2-label text-[11px] font-semibold uppercase select-none leading-[1.4]"
            style="writing-mode: vertical-rl; text-orientation: upright; letter-spacing: 0.08em; white-space: nowrap;"
        >
            {{ open ? 'Close' : 'ASK AI' }}
        </span>

        <!-- Bottom chevron — points LEFT when closed (slide in), RIGHT when open (push out) -->
        <ChevronRight
            :size="16"
            :stroke-width="2.25"
            class="transition-transform duration-[420ms]"
            :class="open ? 'rotate-0' : 'rotate-180'"
        />
    </button>
</template>

<style scoped>
.aia-d2-trigger {
    /* Closed: a tab on the screen's right edge. Open: rides the drawer's left
     * edge. Position is driven by data-open + the --aia-d2-drawer-w var (set
     * from the drawer width) so it tracks the panel responsively. */
    right: 0;
    transform: translateY(-50%);
    will-change: right, transform, box-shadow;
    transition:
        right 420ms cubic-bezier(0.32, 0.72, 0, 1),
        transform 420ms cubic-bezier(0.32, 0.72, 0, 1),
        box-shadow 300ms ease-out;
}
.aia-d2-trigger[data-open='true'] {
    /* --aia-d2-drawer-w is set inline from the drawer width; fallback keeps it
     * resolvable for static analysis and if the binding is ever absent. */
    right: var(--aia-d2-drawer-w, min(420px, 100%));
}
.aia-d2-trigger:hover {
    box-shadow: -16px 0 50px -12px rgba(192, 38, 211, 0.6);
}

/* Mobile: the drawer is full width, so there's no room for a side close
 * grabber — hide it while open and let the header X handle closing. The
 * trigger still appears (as "ASK AI") when the drawer is closed. */
@media (max-width: 640px) {
    .aia-d2-trigger[data-open='true'] {
        display: none;
    }
}
.aia-d2-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 12px;
    height: 12px;
    border-radius: 9999px;
    background: currentColor;
    border: 2px solid #ffffff;
    /* Solid color glow so the green/red reads clearly against the gradient. */
    box-shadow: 0 0 6px 1px currentColor;
    animation: aia-d2-badge-pulse 1.8s ease-out infinite;
}
@keyframes aia-d2-badge-pulse {
    0%   { box-shadow: 0 0 6px 1px currentColor, 0 0 0 0 currentColor; }
    70%  { box-shadow: 0 0 6px 1px currentColor, 0 0 0 7px transparent; }
    100% { box-shadow: 0 0 6px 1px currentColor, 0 0 0 0 transparent; }
}
@media (prefers-reduced-motion: reduce) {
    .aia-d2-trigger {
        transition-duration: 0ms;
    }
}
</style>
